import React, { useContext, useRef } from "react"
import { Link, matchPath, useLocation, useParams } from "react-router-dom"
import {
  Box,
  Fade,
  Paper,
  IconButton,
  ClickAwayListener,
  useMediaQuery,
  Breakpoint
} from "@mui/material"
import ProductsDropDown from "../ProductsDropDown"
import { useTheme } from "@mui/material/styles"
import getMainNavLinks from "../../../../../mp-core/helpers/navLinks/getMainNavLinks"
import ProgramsDropDown from "../ProgramsDropDown"
import ModuleTabs from "../ModuleTabs"
import ProfileDropDown from "../ProfileDropDown/ProfileDropDown"
import { BarsIcon, FaIcon } from "../../../../../mp-core/icons"
import { AssessmentSet, LicensesSet, User } from "../../../../../mp-database"
import { BaseHeader, LinkType, MainNavLinksType, ProgramsContext } from ".."
import { AppContext } from "../../../../../mp-core/context"
import SkipLinks, {
  SKIP_LINKS_SELECTORS
} from "../../../../../mp-core/components/SkipLinks"
import { UserGlobalSearch } from "../../../../../user-management/components"
import { useStyles } from "./styles"
import {
  productAndProgramConfig,
  ShowDropdownsType
} from "../ProductAndProgramConfig"
import { Roles } from "../../../../../mp-utils/generic-utils"
// @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
import { MANAGE_ORG_PATH } from "../../../../../user-management/components/UserGlobalSearch/helpers"
import { MASTERYPREP_HELP_CENTER_HOMEPAGE_URL } from "../../../../../mp-core/constants/urls"
import { useSkipLinks } from "./hooks"

const PATHS_WITHOUT_DEFAULT_HEADER = ["/snap/author/qti-authoring/editor"]

type MainNavParamsType = {
  page: string
  module: string
  program: string
  subPage: string
}
interface MainNavProps {
  licenses?: LicensesSet[]
  user?: User
  assessmentSets?: { [key: string]: AssessmentSet }
  product?: "truscore" | "mastery-resources"
}
const MainNav: React.FC<React.PropsWithChildren<MainNavProps>> = ({
  user,
  assessmentSets,
  licenses
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const isScreenSM = useMediaQuery(theme.breakpoints.down("md" as Breakpoint), {
    noSsr: true
  })

  const productsDropdownRef = useRef<HTMLDivElement>()
  const programsDropdownRef = useRef<HTMLDivElement>()

  const [sideBarOpen, setSideBarOpen] = React.useState(false)

  const params = useParams<MainNavParamsType>()
  const { page, module, program, subPage } = params

  const location = useLocation<Location>()

  const searchParams = new URLSearchParams(location.search)
  const origin = searchParams.get("origin")
  // Only show MP Logo if there is no origin (new platform) or if coming from EOC
  const shouldShowMpLogo = !origin || origin === "eoc"

  const { currentProgram, checkProgramType } = useContext(ProgramsContext)
  const ctx = useContext(AppContext)

  const { skipLinks } = useSkipLinks(
    productsDropdownRef,
    programsDropdownRef,
    user
  )

  const headerConfig = ctx.state.header
  const links = getMainNavLinks({
    user,
    licenses: licenses ?? [],
    location,
    assessmentSets,
    params,
    currentProgram,
    checkProgramType
  }) as MainNavLinksType

  let activePage: LinkType | undefined
  let activeModuleTab: any

  for (const key in links) {
    if (links[key] instanceof Array) {
      links[key].forEach((link: LinkType) => {
        if (link?.key === page) {
          activePage = link
        }
      })
    } else if (links[key] instanceof Object) {
      if (links[key]?.key === page) {
        activePage = links[key]
      }
    } else {
      console.error("links[key] is not an array or object", links[key])
    }
  }

  if (activePage) {
    activeModuleTab = activePage?.children?.find((child: any) => {
      if (child?.key === module || child?.key === activePage?.key) {
        return child
      }
    })
  }
  let pageName = "--"
  if (activePage?.name) {
    pageName = activePage?.name
  }
  if (page === "help") {
    pageName = "Help"
  }

  function renderDropDowns() {
    const m = module ?? "none"
    const sp = subPage ?? "none"

    const showDropdowns: ShowDropdownsType = productAndProgramConfig
    const moduleConfig = showDropdowns?.[page]?.[m]
    const subpageConfig =
      moduleConfig?.[sp] ?? moduleConfig?.[program?.toLowerCase()]
    const showProductsDropdown = subpageConfig?.includes("products") ?? false
    const showProgramsDropdown = subpageConfig?.includes("programs") ?? false

    return (
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        className={classes.dropdowns}
      >
        {user.role === Roles.Super_Administrator &&
          // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
          matchPath(location.pathname, { path: MANAGE_ORG_PATH }) && (
            <UserGlobalSearch />
          )}

        {showProductsDropdown && (
          <ProductsDropDown
            ref={productsDropdownRef}
            user={user}
            links={links}
            pageName={pageName}
          />
        )}
        {showProgramsDropdown && (
          <ProgramsDropDown ref={programsDropdownRef} links={links} />
        )}
      </Box>
    )
  }
  function renderResponsiveSidebar() {
    return (
      <ClickAwayListener
        onClickAway={() => sideBarOpen && setSideBarOpen(false)}
      >
        <Box>
          <IconButton
            style={{ padding: theme.spacing(2) }}
            onClick={() => setSideBarOpen(!sideBarOpen)}
            size="large"
          >
            <BarsIcon style={{ color: "#fff", fontSize: "18px" }} />
          </IconButton>
          <Fade in={sideBarOpen} unmountOnExit={false}>
            <Box position="relative">
              <Paper className={classes.sideBar} elevation={1}>
                {renderDropDowns()}
                <ModuleTabs
                  activePage={activePage}
                  module={module}
                  orientation="vertical"
                />
              </Paper>
            </Box>
          </Fade>
        </Box>
      </ClickAwayListener>
    )
  }

  const isDefaultHeaderHidden = !!PATHS_WITHOUT_DEFAULT_HEADER.find(
    path =>
      !!matchPath(location.pathname, {
        path
      })
  )

  return (
    <>
      <SkipLinks outterTargets={skipLinks} />

      <BaseHeader
        hideDefaultContent={headerConfig.hideContent || isDefaultHeaderHidden}
      >
        {isScreenSM && renderResponsiveSidebar()}
        {shouldShowMpLogo && (
          <>
            <Box
              marginRight={isScreenSM ? 0 : 6}
              className={classes.logo}
              aria-label="Go to Home"
            >
              <Link to="/" style={{ display: "flex" }}>
                <img
                  src={"/img/header-hat-logo-white.svg"}
                  alt="MasteryPrep Logo"
                  id="mainNav-Logo-click-to-go-home"
                  data-automation-id="homepage-logo"
                />
              </Link>
            </Box>

            <Box
              component="nav"
              className={classes.navbar}
              aria-label="module-tabs-navigation"
            >
              {!isScreenSM && (
                <Box flex={1}>
                  <ModuleTabs
                    id={SKIP_LINKS_SELECTORS.navigationTopBar.value}
                    activePage={activePage}
                    module={module}
                  />
                </Box>
              )}

              {!isScreenSM && renderDropDowns()}

              {user?.role !== Roles.Student && (
                <IconButton
                  className={classes.questionIconButton}
                  href={MASTERYPREP_HELP_CENTER_HOMEPAGE_URL}
                  target="_blank"
                  aria-label="Go to Support"
                >
                  <FaIcon solid name="question" />
                </IconButton>
              )}

              <Box
                display="flex"
                alignItems="center"
                margin={isScreenSM ? 0 : theme.spacing(0, 0, 1, 1)}
                // TODO: remove the margin below
                marginLeft={isScreenSM ? 0 : 2}
              >
                <ProfileDropDown user={user} />
              </Box>
            </Box>
          </>
        )}
      </BaseHeader>
    </>
  )
}
export default MainNav
